import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * IB-875 — Column-visibility hook for the shared R&E table.
 *
 * Name / Total / trailing are pinned and never appear here. Only the optional
 * columns are toggleable:
 *   venue → cash, vik, description, price
 *   event → source, cash, vik
 *
 * Persisted per (context, event) under a versioned localStorage key. Self-heals:
 * a missing / malformed / partial stored value resets to defaults (mirrors
 * ExpenditureFiltersDropdown.loadFilters). VIK is additionally gated at render
 * time by the event's vik_enabled flag — this hook only tracks the toggle.
 */
const DEFINITIONS = {
    venue: [
        { key: 'description', label: 'Description', default: false },
        { key: 'price', label: 'Price', default: false },
        { key: 'qty', label: 'Qty', default: true },
        { key: 'cash', label: 'Cash', default: true },
        { key: 'vik', label: 'VIK', default: true },
    ],
    event: [
        { key: 'source', label: 'Source', default: true },
        { key: 'qty', label: 'Qty', default: true },
        { key: 'cash', label: 'Cash', default: true },
        { key: 'vik', label: 'VIK', default: true },
    ],
};

export default function useREColumns(context, eventId) {
    const definitions = useMemo(() => DEFINITIONS[context] ?? DEFINITIONS.venue, [context]);

    const defaults = useMemo(() => {
        const base = {};
        definitions.forEach((def) => {
            base[def.key] = def.default;
        });
        return base;
    }, [definitions]);

    const storageKey = `re-columns-${context}-${eventId ?? 'na'}-v1`;

    const load = useCallback(() => {
        try {
            const raw = window.localStorage.getItem(storageKey);
            if (!raw) return defaults;

            const parsed = JSON.parse(raw);
            if (!parsed || typeof parsed !== 'object') return defaults;

            // Self-heal: every expected key must be present, otherwise reset.
            const healed = {};
            let complete = true;
            definitions.forEach((def) => {
                if (typeof parsed[def.key] === 'boolean') {
                    healed[def.key] = parsed[def.key];
                } else {
                    complete = false;
                }
            });

            return complete ? healed : defaults;
        } catch (e) {
            return defaults;
        }
    }, [storageKey, defaults, definitions]);

    const [columns, setColumns] = useState(load);

    // Re-load when the storage key changes (context / event switch).
    useEffect(() => {
        setColumns(load());
    }, [storageKey]);

    const persist = useCallback(
        (next) => {
            try {
                window.localStorage.setItem(storageKey, JSON.stringify(next));
            } catch (e) {
                // Ignore quota / privacy-mode failures — visibility still works
                // for the session.
            }
        },
        [storageKey],
    );

    const toggle = useCallback(
        (key) => {
            setColumns((prev) => {
                const next = { ...prev, [key]: !prev[key] };
                persist(next);
                return next;
            });
        },
        [persist],
    );

    return { columns, toggle, definitions };
}
